import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Paper,
  Grid2,
} from "@mui/material";
import adminAPI from "../../Services/Admin/adminAPI";
import SettingsIcon from "@mui/icons-material/Settings";
import TypeUpdateDialog from "../Small/TypeUpdateDialog";
import SubTypeUpdateDialog from "../Small/SubTypeUpdateDialog";
import PostNomenclatureCard from "../Small/PostNomenclatureCard";

const ProductCard = () => {
  const specialities = [
    { value: "", title: "Aucune" },
    { value: "ortho", title: "Chirurgie orthopédique" },
    { value: "dig", title: "Chirurgie digestive" },
    { value: "general", title: "Chirurgie générale" },
    { value: "uro", title: "Chirurgie urologique" },
    { value: "vasc", title: "Chirurgie vasculaire" },
    { value: "thor", title: "Chirurgie thoracique" },
    { value: "plastic", title: "Chirurgie plastique" },
    { value: "neuro", title: "Neurochirurgie" },
    { value: "transp", title: "Transplantation" },
  ];

  const types = [
    { value: "2", title: "Traumatologie" },
    { value: "1", title: "Elective" },
  ];

  const subTypes = [
    { value: "", title: "Aucune" },
    { value: "shoulder", title: "Épaule" },
    { value: "humerus", title: "Humérus" },
    { value: "elbow", title: "Coude" },
    { value: "forearm", title: "Avant-bras" },
    { value: "wristhand", title: "Poignet-Main" },
    { value: "back", title: "Rachis" },
    { value: "pelvic", title: "Bassin" },
    { value: "hip", title: "Hanche" },
    { value: "proximalFemur", title: "Fémur proximal" },
    { value: "midFemur", title: "Diaphyse fémorale" },
    { value: "distalFemur", title: "Fémur distal" },
    { value: "knee", title: "Genou" },
    { value: "limb", title: "Jambe" },
    { value: "ankle", title: "Cheville" },
    { value: "foot", title: "Pied" },
  ];

  const nomenclatureList = useRef([]);
  const [filteredNomenclatureList, setFilteredNomenclatureList] = useState([]);
  const [speciality, setSpeciality] = useState("ortho");
  const [subType, setSubType] = useState("");
  const [status, setStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [showColumns, setShowColumns] = useState(() => {
    const savedColumns = localStorage.getItem("showColumns");
    return savedColumns
      ? JSON.parse(savedColumns)
      : {
          speciality: true,
          codeAmbulant: true,
          codeHospitalisation: true,
          n: true,
          name: true,
          type: true,
          subType: true,
        };
  });
  const [showDialog, setShowDialog] = useState(false);

  const updateFilteredNomenclatureList = useCallback(() => {
    const filteredList = nomenclatureList.current.filter((item) => {
      const matchesSearchTerm =
        item.codeAmbulant.includes(searchTerm) ||
        item.codeHospitalisation.includes(searchTerm) ||
        item.n.includes(searchTerm) ||
        item.name.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        status === "" ||
        (status === "Complet" &&
          item.codeAmbulant &&
          item.codeHospitalisation &&
          item.n &&
          item.name &&
          item.type) ||
        (status === "Incomplet" &&
          (!item.codeAmbulant ||
            !item.codeHospitalisation ||
            !item.n ||
            !item.name ||
            !item.type));

      const matchesSubType = subType === "" || item.subType === subType;

      return matchesSearchTerm && matchesStatus && matchesSubType;
    });

    setFilteredNomenclatureList(filteredList);
  }, [searchTerm, status, subType]);

  const fetchNomenclature = useCallback(
    async (speciality) => {
      try {
        const data = await adminAPI.fetchNomenclature(speciality);
        nomenclatureList.current = data;
        updateFilteredNomenclatureList();
      } catch (error) {
        console.log(error.response);
      }
    },
    [updateFilteredNomenclatureList]
  );

  useEffect(() => {
    if (speciality) {
      fetchNomenclature(speciality);
    }
  }, [speciality, fetchNomenclature]);

  useEffect(() => {
    localStorage.setItem("showColumns", JSON.stringify(showColumns));
  }, [showColumns]);

  useEffect(() => {
    updateFilteredNomenclatureList();
  }, [searchTerm, status, subType, updateFilteredNomenclatureList]);

  const handleSpecialityChange = (event) => {
    setSpeciality(event.target.value);
  };

  const handleSubTypeChange = (event) => {
    setSubType(event.target.value);
  };

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = filteredNomenclatureList.map((item) => item.id);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prevSelectedIds) =>
      prevSelectedIds.includes(id)
        ? prevSelectedIds.filter((selectedId) => selectedId !== id)
        : [...prevSelectedIds, id]
    );
  };

  const getTypeTitle = (value) => {
    const type = types.find((type) => type.value === value);
    return type ? type.title : "";
  };

  const getSubTypeTitle = (value) => {
    const subType = subTypes.find((subType) => subType.value === value);
    return subType ? subType.title : "";
  };

  const handleShowColumnsDialog = () => {
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
  };

  const handleColumnChange = (column) => {
    setShowColumns({
      ...showColumns,
      [column]: !showColumns[column],
    });
  };

  const [isTypeUpdateDialogOpen, setIsTypeUpdateDialogOpen] = useState(false);
  const handleOpenTypeUpdateDialog = () => {
    setIsTypeUpdateDialogOpen(true);
  };
  const handleCloseTypeUpdateDialog = () => {
    setIsTypeUpdateDialogOpen(false);
  };

  const [isSubTypeUpdateDialogOpen, setIsSubTypeUpdateDialogOpen] =
    useState(false);
  const handleOpenSubTypeUpdateDialog = () => {
    setIsSubTypeUpdateDialogOpen(true);
  };
  const handleCloseSubTypeUpdateDialog = () => {
    setIsSubTypeUpdateDialogOpen(false);
  };

  const [isPostDialogOpen, setIsPostDialogOpen] = useState(false);
  const handleOpenPostDialog = () => {
    setIsPostDialogOpen(true);
  };
  const handleClosePostDialog = () => {
    setIsPostDialogOpen(false);
  };

  return (
    <Card sx={{ maxWidth: 1600, margin: "auto", padding: { xs: 0, md: 2 } }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Filtres
        </Typography>
        <Grid2 container spacing={2} mb={3}>
          <Grid2 size={{ xs: 12, md: 4 }}>
            {" "}
            <FormControl fullWidth>
              <InputLabel>Spécialité</InputLabel>
              <Select value={speciality} onChange={handleSpecialityChange}>
                {specialities.map((speciality) => (
                  <MenuItem key={speciality.value} value={speciality.value}>
                    {speciality.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            {speciality === "ortho" && (
              <FormControl name="localisation" fullWidth>
                <InputLabel>Région</InputLabel>
                <Select value={subType} onChange={handleSubTypeChange}>
                  {subTypes.map((subType) => (
                    <MenuItem key={subType.value} value={subType.value}>
                      {subType.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={status} onChange={handleStatusChange}>
                <MenuItem value="">Aucun</MenuItem>
                <MenuItem value="Complet">Complet</MenuItem>
                <MenuItem value="Incomplet">Incomplet</MenuItem>
              </Select>
            </FormControl>
          </Grid2>
        </Grid2>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher par code ambulant, hospitalisation, N, nom"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ mb: 3 }}
        />
        <Box display="flex" justifyContent="space-between" mb={3}>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenPostDialog}
            >
              + Ajouter
            </Button>

            <IconButton
              variant="outlined"
              color="secondary"
              onClick={handleShowColumnsDialog}
            >
              <SettingsIcon />
            </IconButton>
          </Stack>

          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleOpenTypeUpdateDialog}
            >
              Type
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleOpenSubTypeUpdateDialog}
            >
              Sous-type
            </Button>
          </Stack>
        </Box>

        <Dialog open={showDialog} onClose={handleCloseDialog}>
          <DialogTitle>Afficher les colonnes</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              {Object.keys(showColumns).map((column) => (
                <FormControlLabel
                  key={column}
                  control={
                    <Checkbox
                      checked={showColumns[column]}
                      onChange={() => handleColumnChange(column)}
                    />
                  }
                  label={column.charAt(0).toUpperCase() + column.slice(1)}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Fermer
            </Button>
          </DialogActions>
        </Dialog>

        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={
                        selectedIds.length > 0 &&
                        selectedIds.length < filteredNomenclatureList.length
                      }
                      checked={
                        filteredNomenclatureList.length > 0 &&
                        selectedIds.length === filteredNomenclatureList.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  {showColumns.speciality && <TableCell>Spécialité</TableCell>}
                  {showColumns.codeAmbulant && (
                    <TableCell>Code ambulant</TableCell>
                  )}
                  {showColumns.codeHospitalisation && (
                    <TableCell>Code hospitalisation</TableCell>
                  )}
                  {showColumns.n && <TableCell>N</TableCell>}
                  {showColumns.name && <TableCell>Nom</TableCell>}
                  {showColumns.type && <TableCell>Type</TableCell>}
                  {showColumns.subType && <TableCell>Sous-type</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredNomenclatureList.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => handleSelectOne(item.id)}
                      />
                    </TableCell>
                    {showColumns.speciality && (
                      <TableCell>{item.speciality}</TableCell>
                    )}
                    {showColumns.codeAmbulant && (
                      <TableCell>{item.codeAmbulant}</TableCell>
                    )}
                    {showColumns.codeHospitalisation && (
                      <TableCell>{item.codeHospitalisation}</TableCell>
                    )}
                    {showColumns.n && <TableCell>{item.n}</TableCell>}
                    {showColumns.name && <TableCell>{item.name}</TableCell>}
                    {showColumns.type && (
                      <TableCell>{getTypeTitle(item.type)}</TableCell>
                    )}
                    {showColumns.subType && (
                      <TableCell>{getSubTypeTitle(item.subType)}</TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <TypeUpdateDialog
          open={isTypeUpdateDialogOpen}
          handleClose={handleCloseTypeUpdateDialog}
          selectedIds={selectedIds}
        />
        <SubTypeUpdateDialog
          open={isSubTypeUpdateDialogOpen}
          handleClose={handleCloseSubTypeUpdateDialog}
          selectedIds={selectedIds}
        />
        <PostNomenclatureCard
          open={isPostDialogOpen}
          handleClose={handleClosePostDialog}
        />
      </CardContent>
    </Card>
  );
};

export default ProductCard;
